/**
 * ForwardMessageModal — Seleziona una chat per inoltrare un messaggio
 * Notion-style design
 */

import React, { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Users, Send } from 'lucide-react';
import { useTeamChatStore, TeamChatMessage } from '../../lib/stores/teamChatStore';
import { useAuth } from '../../lib/auth';
import toast from 'react-hot-toast';

interface ForwardMessageModalProps {
  message: TeamChatMessage;
  onClose: () => void;
}

export function ForwardMessageModal({ message, onClose }: ForwardMessageModalProps) {
  const { userProfile } = useAuth();
  const { chats, forwardMessage } = useTeamChatStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [forwarding, setForwarding] = useState(false);

  const getChatDisplayName = useCallback((c: typeof chats[0]) => {
    if (c.is_group && c.name) return c.name;
    const other = c.participants?.find(p => p.user_id !== userProfile?.id);
    return other?.user?.full_name || 'Chat';
  }, [userProfile?.id]);

  const filteredChats = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return chats
      .filter(c => c.id !== message.chat_id) // Escludi chat corrente
      .filter(c => !q || getChatDisplayName(c).toLowerCase().includes(q));
  }, [chats, searchQuery, getChatDisplayName, message.chat_id]);

  const handleForward = useCallback(async () => {
    if (!selectedChatId) return;
    setForwarding(true);
    try {
      await forwardMessage(message.id, message.chat_id, selectedChatId);
      toast.success('Messaggio inoltrato');
      onClose();
    } catch {
      toast.error('Errore nell\'inoltro');
    } finally {
      setForwarding(false);
    }
  }, [selectedChatId, message, forwardMessage, onClose]);

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={onClose} />
      <div
        className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 max-h-[70vh] bg-white rounded-xl flex flex-col"
        style={{ border: '1px solid rgba(55,53,47,0.09)', boxShadow: 'rgba(55,53,47,0.1) 0px 8px 32px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(55,53,47,0.09)' }}>
          <span className="font-semibold text-sm" style={{ color: '#37352F' }}>Inoltra messaggio</span>
          <button onClick={onClose} className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)]">
            <X className="w-4 h-4" style={{ color: '#787774' }} strokeWidth={1.5} />
          </button>
        </div>

        {/* Preview messaggio */}
        <div className="px-4 py-2" style={{ backgroundColor: '#F7F7F5', borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
          <div className="text-[11px] font-medium mb-0.5" style={{ color: '#787774' }}>
            {message.sender?.full_name}
          </div>
          <div className="text-[12px] line-clamp-2" style={{ color: '#37352F' }}>
            {(() => {
              if (message.message_type === 'crm_share') {
                try {
                  const p = JSON.parse(message.content);
                  const labels: Record<string, string> = { lead: '👤', task: '✅', project: '📁', deal: '💰' };
                  return `${labels[p.entity_type] || '📎'} ${p.entity_data?.name || p.entity_data?.title || 'Condivisione'}`;
                } catch { return '📎 Condivisione'; }
              }
              if (message.message_type === 'task_comment') {
                try {
                  const p = JSON.parse(message.content);
                  return `💬 ${p.author_name || 'Commento'}: ${p.comment_content || ''}`;
                } catch { return '💬 Commento task'; }
              }
              if (message.message_type === 'whatsapp_forward') {
                try {
                  const p = JSON.parse(message.content);
                  return `📱 ${p.senderName || 'WhatsApp'}: ${p.text || ''}`;
                } catch { return '📱 Inoltro WhatsApp'; }
              }
              return message.content || (message.attachments?.[0] ? `[${message.attachments[0].type}]` : '');
            })()}
          </div>
        </div>

        {/* Search */}
        <div className="px-3 py-2" style={{ borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
          <div className="relative flex items-center">
            <Search className="absolute left-2.5 w-3.5 h-3.5 pointer-events-none" style={{ color: '#9B9A97' }} strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Cerca chat..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md outline-none"
              style={{ backgroundColor: 'rgba(55,53,47,0.06)', color: '#37352F', fontSize: '13px', border: '1px solid transparent' }}
              autoFocus
            />
          </div>
        </div>

        {/* Chat list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {filteredChats.length === 0 ? (
            <p className="text-xs px-4 py-6 text-center" style={{ color: '#9B9A97' }}>Nessuna chat</p>
          ) : (
            filteredChats.map(c => {
              const name = getChatDisplayName(c);
              const isSelected = c.id === selectedChatId;
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedChatId(c.id)}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors"
                  style={{ backgroundColor: isSelected ? 'rgba(35,131,226,0.1)' : 'transparent' }}
                  onMouseEnter={e => !isSelected && (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)')}
                  onMouseLeave={e => !isSelected && (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  {c.is_group ? (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(35,131,226,0.1)' }}>
                      <Users className="w-4 h-4" style={{ color: '#2383E2' }} strokeWidth={1.5} />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium" style={{ backgroundColor: 'rgba(55,53,47,0.08)', color: '#37352F' }}>
                      {getInitials(name)}
                    </div>
                  )}
                  <span className="text-[13px] truncate" style={{ color: isSelected ? '#2383E2' : '#37352F', fontWeight: isSelected ? 600 : 500 }}>
                    {name}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 flex justify-end" style={{ borderTop: '1px solid rgba(55,53,47,0.09)' }}>
          <button
            onClick={handleForward}
            disabled={!selectedChatId || forwarding}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#2383E2' }}
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
            {forwarding ? 'Invio...' : 'Inoltra'}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
